import { useEffect, useLayoutEffect, useRef, useState } from 'react';

type Tip = { text: string; rect: DOMRect };

const SHOW_DELAY = 300;
const GAP = 8;
const EDGE = 8;

export function TooltipHost() {
  const [tip, setTip] = useState<Tip | null>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; below: boolean } | null>(null);

  useEffect(() => {
    let current: HTMLElement | null = null;
    let saved = '';
    let timer: number | undefined;

    const release = () => {
      window.clearTimeout(timer);
      if (current && current.getAttribute('title') === '') current.setAttribute('title', saved);
      current = null;
      saved = '';
      setTip(null);
    };

    const grab = (target: EventTarget | null) => {
      const el = target instanceof Element ? (target.closest('[title]') as HTMLElement | null) : null;
      if (el === current) return;
      release();
      const text = el?.getAttribute('title')?.trim();
      if (!el || !text) return;
      current = el;
      saved = el.getAttribute('title') ?? '';
      el.setAttribute('title', '');
      timer = window.setTimeout(() => {
        if (current === el && el.isConnected) setTip({ text, rect: el.getBoundingClientRect() });
      }, SHOW_DELAY);
    };

    const onOver = (e: MouseEvent) => grab(e.target);
    const onOut = (e: MouseEvent) => {
      if (!current) return;
      const to = e.relatedTarget;
      if (to instanceof Node && current.contains(to)) return;
      release();
    };
    const onFocusIn = (e: FocusEvent) => {
      if (e.target instanceof HTMLElement && e.target.matches(':focus-visible')) grab(e.target);
    };

    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', release);
    document.addEventListener('mousedown', release, true);
    window.addEventListener('scroll', release, true);
    return () => {
      release();
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', release);
      document.removeEventListener('mousedown', release, true);
      window.removeEventListener('scroll', release, true);
    };
  }, []);

  useLayoutEffect(() => {
    const bubble = bubbleRef.current;
    if (!tip || !bubble) {
      setPos(null);
      return;
    }
    const { width, height } = bubble.getBoundingClientRect();
    const below = tip.rect.top - height - GAP < EDGE;
    const top = below ? tip.rect.bottom + GAP : tip.rect.top - height - GAP;
    const center = tip.rect.left + tip.rect.width / 2;
    const left = Math.max(EDGE, Math.min(center - width / 2, window.innerWidth - width - EDGE));
    setPos({ left, top, below });
  }, [tip]);

  if (!tip) return null;
  return (
    <div
      ref={bubbleRef}
      className={`tooltip${pos?.below ? ' below' : ''}`}
      role="tooltip"
      style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: -9999 }}
    >
      {tip.text}
    </div>
  );
}
